/**
 * TilesetConfig.ts - Terrain type to tile ID mappings
 * Maps game terrain types to specific tiles in the world_tileset.png
 */

export enum TerrainType {
  GRASS = 0,
  WATER = 1,
  FOREST = 2,
  MOUNTAIN = 3,
  DESERT = 4,
  TOWN = 5,
  CASTLE = 6,
  DUNGEON = 7,
  // Additional terrain types for variety
  GRASS_FLOWERS = 8,
  DEEP_WATER = 9,
  SHORE = 10,
  ROAD = 11,
  BRIDGE = 12,
  SWAMP = 13,
  SNOW = 14,
  LAVA = 15,
}

export interface TileConfig {
  baseId: number;
  variants?: number[];
  frames?: number[];
  frameTime?: number;
  walkable: boolean;
  encounterRate?: number; // 0-100, chance of random encounter per step
}

// Tile configuration mapping terrain types to tile IDs
// Tileset layout: 16x16 tiles in a 256x256 image (16 tiles per row)
export const WORLD_TILESET: Record<TerrainType, TileConfig> = {
  [TerrainType.GRASS]: {
    baseId: 0,
    variants: [0, 1, 2, 3], // Random grass variations
    walkable: true,
    encounterRate: 8,
  },
  [TerrainType.WATER]: {
    baseId: 16,
    frames: [16, 17, 18, 17], // Animated water
    frameTime: 300,
    walkable: false,
  },
  [TerrainType.FOREST]: {
    baseId: 32,
    variants: [32, 33, 34], // Different tree types
    walkable: true,
    encounterRate: 15, // Higher encounter rate in forests
  },
  [TerrainType.MOUNTAIN]: {
    baseId: 48,
    variants: [48, 49],
    walkable: false,
  },
  [TerrainType.DESERT]: {
    baseId: 64,
    variants: [64, 65, 66],
    walkable: true,
    encounterRate: 10,
  },
  [TerrainType.TOWN]: {
    baseId: 80,
    walkable: true,
    encounterRate: 0, // No encounters in towns
  },
  [TerrainType.CASTLE]: {
    baseId: 96,
    walkable: true,
    encounterRate: 0,
  },
  [TerrainType.DUNGEON]: {
    baseId: 112,
    walkable: true,
    encounterRate: 0, // Entrance only, dungeons have own encounter rates
  },
  [TerrainType.GRASS_FLOWERS]: {
    baseId: 4,
    variants: [4, 5, 6],
    walkable: true,
    encounterRate: 8,
  },
  [TerrainType.DEEP_WATER]: {
    baseId: 19,
    frames: [19, 20, 21, 20],
    frameTime: 400,
    walkable: false, // Requires ship
  },
  [TerrainType.SHORE]: {
    baseId: 22,
    variants: [22, 23, 24, 25], // Different shore orientations
    walkable: false,
  },
  [TerrainType.ROAD]: {
    baseId: 128,
    variants: [128, 129], // Vertical, horizontal
    walkable: true,
    encounterRate: 0, // Safe travel on roads
  },
  [TerrainType.BRIDGE]: {
    baseId: 130,
    walkable: true,
    encounterRate: 0,
  },
  [TerrainType.SWAMP]: {
    baseId: 144,
    frames: [144, 145, 146, 145],
    frameTime: 500,
    walkable: true,
    encounterRate: 20, // Very high encounter rate
  },
  [TerrainType.SNOW]: {
    baseId: 160,
    variants: [160, 161],
    walkable: true,
    encounterRate: 12,
  },
  [TerrainType.LAVA]: {
    baseId: 176,
    frames: [176, 177, 178, 177],
    frameTime: 200,
    walkable: false, // Damage if walked on without protection
  },
};

// Fallback colors for when sprites aren't loaded
export const TERRAIN_COLORS: Record<TerrainType, string> = {
  [TerrainType.GRASS]: '#22aa22',
  [TerrainType.WATER]: '#2266ff',
  [TerrainType.FOREST]: '#116611',
  [TerrainType.MOUNTAIN]: '#888888',
  [TerrainType.DESERT]: '#ddcc66',
  [TerrainType.TOWN]: '#aa6622',
  [TerrainType.CASTLE]: '#cccccc',
  [TerrainType.DUNGEON]: '#443333',
  [TerrainType.GRASS_FLOWERS]: '#44bb44',
  [TerrainType.DEEP_WATER]: '#1144aa',
  [TerrainType.SHORE]: '#aabb99',
  [TerrainType.ROAD]: '#998866',
  [TerrainType.BRIDGE]: '#886644',
  [TerrainType.SWAMP]: '#556633',
  [TerrainType.SNOW]: '#eeffff',
  [TerrainType.LAVA]: '#ff4400',
};

// Get a random variant tile ID for terrain that has variants
export function getTileVariant(terrain: TerrainType, x: number, y: number): number {
  const config = WORLD_TILESET[terrain];
  if (!config) return 0;

  if (config.variants && config.variants.length > 0) {
    // Use position-based pseudo-random for consistent appearance
    const seed = (x * 7919 + y * 7927) % config.variants.length;
    const variant = config.variants[seed];
    return variant ?? config.baseId;
  }

  return config.baseId;
}

// Get animated tile frame based on time
export function getAnimatedTileFrame(terrain: TerrainType, time: number): number {
  const config = WORLD_TILESET[terrain];
  if (!config || !config.frames || config.frames.length === 0) return config?.baseId ?? 0;

  const frameTime = config.frameTime ?? 300;
  const frameIndex = Math.floor(time / frameTime) % config.frames.length;
  const frame = config.frames[frameIndex];
  return frame ?? config.baseId;
}

// Check if terrain is walkable
export function isTerrainWalkable(terrain: TerrainType): boolean {
  return WORLD_TILESET[terrain]?.walkable ?? false;
}

// Get encounter rate for terrain
export function getEncounterRate(terrain: TerrainType): number {
  return WORLD_TILESET[terrain]?.encounterRate ?? 0;
}
